import { getProperty } from 'dot-prop';
import { getNumericPathIndices } from '~/dashboard/views/workflows/service/actions';
import {
  createStringOfPathArray,
  getExpressionFunction,
  getIsExpressionGroupAwaitingOperands,
  getIsExpressionGroupWithoutOperators,
  getIsExpressionFunctionString,
  getIsExpressionOperatorString,
} from '~/dashboard/views/workflows/store/engine/utils';
import type { Expression, ExpressionTerm } from '~/dashboard/views/workflows/store/engine/types';

export enum ExpressionContainerType {
  /** a min/max group: stored as [a, 'min', b], shown as Min ( a , b ) */
  FUNCTION = 'function',
  PLAIN = 'plain',
  ROOT = 'root',
}

export type ExpressionContainer = {
  path: string;
  type: ExpressionContainerType;
  /** which function this is, 'min' or 'max'. Undefined for an ordinary group. */
  operator?: string;
  /** nothing in it yet: either empty, or holding only its own 'min'/'max' */
  awaitingOperands: boolean;
  /** contains an operator, so moving its contents into the parent could change the result */
  holdsOperators: boolean;
  items: (ExpressionTerm | ExpressionTerm[])[];
};

export type ExpressionNode = {
  path: string;
  item: ExpressionTerm | ExpressionTerm[];
  isGroup: boolean;
  /** this group's own 'min'/'max', which the editor draws as a comma */
  isSeparator: boolean;
  /** + - * / , or a 'min'/'max' that belongs to some other group */
  isOperator: boolean;
  isOperand: boolean;
  container: ExpressionContainer;
  group?: ExpressionContainer;
};

const describeContainer = (expressions: Expression, path: string): ExpressionContainer => {
  const group = path ? getProperty(expressions, path) : expressions;
  // the top level has no parentheses, so a min/max there is a plain operator, not a comma
  const operator = path && Array.isArray(group) ? getExpressionFunction(group) : undefined;

  const unjoinedType = path ? ExpressionContainerType.PLAIN : ExpressionContainerType.ROOT;

  return {
    path,
    operator,
    type: operator ? ExpressionContainerType.FUNCTION : unjoinedType,
    awaitingOperands: getIsExpressionGroupAwaitingOperands(group),
    holdsOperators: Array.isArray(group) && !getIsExpressionGroupWithoutOperators(group),
    items: Array.isArray(group) ? group : [],
  };
};

/**
 * Describes what sits at a path and what encloses it, so callers do not each have to work out whether
 * a position is an operand, a comma or a parenthesis.
 */
export function describeExpressionNode({
  expressions,
  path,
}: {
  expressions?: Expression;
  path?: string;
}): ExpressionNode | undefined {
  if (!expressions) return undefined;

  const indices = getNumericPathIndices(path);
  const containerPath = indices?.length ? createStringOfPathArray(indices.slice(0, -1)) : '';
  const container = describeContainer(expressions, containerPath);
  const item = (
    path ? getProperty(expressions, path) : undefined
  );

  const isGroup = Array.isArray(item);
  const isOperatorString = typeof item === 'string' && getIsExpressionOperatorString(item);
  // a group's own min/max is drawn as a comma, so it does not count as an operator here
  const isSeparator = isOperatorString
    && Boolean(container.operator)
    && item === container.operator;

  return {
    path: path ?? '',
    item,
    isGroup,
    isSeparator,
    isOperator: isOperatorString && !isSeparator,
    isOperand: item !== undefined && !isOperatorString,
    container,
    group: isGroup ? describeContainer(expressions, path) : undefined,
  };
}

export function getIsFunctionContainer(container?: ExpressionContainer) {
  return container?.type === ExpressionContainerType.FUNCTION;
}

export { getIsExpressionFunctionString };
